import { tv, type VariantProps } from "tailwind-variants";

import { Span } from "./Span";

export const hintDotVariants = tv({
	slots: {
		// Transparent box that enlarges the hover/tooltip hit area around the small visible
		// dot. The dot sits at the box's top-right corner so it lands exactly where the box
		// is placed; the hover area extends down-left from it (no outward overflow, so it
		// never triggers scrollbars on a scrollable ancestor).
		hitArea: "inline-flex shrink-0 items-start justify-end",
		dot: "rounded-full",
	},
	variants: {
		size: {
			sm: { hitArea: "size-5", dot: "size-2.5" },
			md: { hitArea: "size-6", dot: "size-3" },
		},
		color: {
			info: { dot: "bg-info-active-ring" },
			primary: { dot: "bg-primary-active-ring" },
			danger: { dot: "bg-danger-active-ring" },
			success: { dot: "bg-success-active-ring" },
			warning: { dot: "bg-warning-active-ring" },
		},
	},
	defaultVariants: {
		size: "md",
		color: "info",
	},
});

export type HintDotVariant = VariantProps<typeof hintDotVariants>;

export type HintDotProps = HintDotVariant & {
	readonly className?: string;
	readonly tooltip?: string;
	readonly id?: string;
};

/**
 * A small dot used to draw attention to a feature or capability, with an optional
 * tooltip. The visible dot sits inside a larger transparent hover/tooltip hit area.
 * Positioning is the parent's responsibility via `className` (applied to the hit area),
 * e.g. absolute placement in a `relative` container to sit on a button's corner.
 * The hit area is a `Span`, so tooltip wiring is reused from the design system.
 */
export function HintDot({ color = "warning", size = "md", className, tooltip, id }: HintDotProps) {
	const { hitArea, dot } = hintDotVariants({ color, size });

	return (
		<Span
			className={hitArea({ className })}
			tooltip={tooltip ? { children: tooltip } : undefined}
		>
			<span id={id} className={dot()} />
		</Span>
	);
}
